import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSignup } from '../../hooks/auth/useSignup';
import { Mail, User, Lock, MapPin, Briefcase } from 'lucide-react';
// Remove the react-toastify import and use your existing toast system
// If you don't have a toast system set up, you can create a simple implementation
// or just use console.log for now

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  city: string;
  experience: string;
  industries: string[];
}

const CITIES = [
  'New York City, NY',
  'Washington, D.C.',
  'San Francisco, CA',
  'Boston, MA',
  'Chicago, IL',
  'Seattle, WA',
  'Miami, FL',
  'Los Angeles, CA',
  'Austin, TX',
  'Houston, TX',
  'Dallas, TX',
  'Atlanta, GA',
  'Toronto, ON',
  'Vancouver, BC',
  'London, UK',
  'Amsterdam, Netherlands',
  'Sydney, Australia',
  'Singapore',
  'USA',
  'Africa',
  'Other Canada',
  'Other Outside of North America',
  'Other'
];

const EXPERIENCE_LEVELS = [
  'College',
  'Early Career',
  'Mid-Level',
  'Senior Level',
  'Executive/Chief'
];

const INDUSTRIES = [
  'Technology',
  'Finance',
  'Healthcare',
  'Education',
  'Manufacturing',
  'Retail',
  'Media',
  'Consulting',
  'Real Estate',
  'Energy',
  'Transportation',
  'Hospitality',
  'Government',
  'Non-profit'
];

export function MultiStepSignupForm() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    city: '',
    experience: '',
    industries: []
  });

  const { isLoading, handleSignup } = useSignup();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleIndustryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({ ...prev, industries: selectedOptions }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If on step 1, validate and go to step 2
    if (step === 1) {
      if (formData.password !== formData.confirmPassword) {
        // Use console.log instead of toast for now
        console.error('Passwords do not match');
        // You can implement your own alert here if needed
        alert('Passwords do not match');
        return;
      }
      
      setStep(2);
      return;
    }
    
    // If on step 2, submit form and redirect on success
    if (step === 2) {
      try {
        const success = await handleSignup(formData.email, formData.password, {
          first_name: formData.firstName,
          last_name: formData.lastName,
          onboarding_data: {
            city: formData.city,
            experience_level: formData.experience,
            industries: formData.industries
          }
        });

        if (success) {
          console.log('Account created successfully!');
          // Directly navigate to subscription page on success
          navigate('/subscription');
        }
      } catch (error) {
        console.error('Signup error:', error);
        alert('Failed to create account. Please try again.');
      }
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            First Name*
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white" />
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className="w-full pl-10 pr-4 py-3 bg-white border border-black rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-accent-green focus:border-accent-green"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Last Name*
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white" />
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className="w-full pl-10 pr-4 py-3 bg-white border border-black rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-accent-green focus:border-accent-green"
              required
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Email Address*
        </label>
        <p className="text-xs text-gray-300 mb-2">
          Please use the email address you'd like us to contact you at
        </p>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white" />
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full pl-10 pr-4 py-3 bg-white border border-black rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-accent-green focus:border-accent-green"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Password*
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white" />
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className="w-full pl-10 pr-4 py-3 bg-white border border-black rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-accent-green focus:border-accent-green"
            required
            minLength={8}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Confirm Password*
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white" />
          <input
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            className="w-full pl-10 pr-4 py-3 bg-white border border-black rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-accent-green focus:border-accent-green"
            required
            minLength={8}
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Closest Major City*
        </label>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white" />
          <select
            name="city"
            value={formData.city}
            onChange={handleChange}
            className="w-full pl-10 pr-4 py-3 bg-white border border-black rounded-xl text-black focus:ring-2 focus:ring-accent-green focus:border-accent-green"
            required
          >
            <option value="">Select city</option>
            {CITIES.map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Professional Workplace Experience*
        </label>
        <div className="relative">
          <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white" />
          <select
            name="experience"
            value={formData.experience}
            onChange={handleChange}
            className="w-full pl-10 pr-4 py-3 bg-white border border-black rounded-xl text-black focus:ring-2 focus:ring-accent-green focus:border-accent-green"
            required
          >
            <option value="">Select experience level</option>
            {EXPERIENCE_LEVELS.map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Industries*
        </label>
        <select
          multiple
          name="industries"
          value={formData.industries}
          onChange={handleIndustryChange}
          className="w-full px-4 py-3 bg-white border border-black rounded-xl text-black focus:ring-2 focus:ring-accent-green focus:border-accent-green h-32"
          required
        >
          {INDUSTRIES.map(industry => (
            <option key={industry} value={industry}>{industry}</option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-300">
          Hold Ctrl/Cmd to select multiple industries
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#2a1114] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="text-center text-3xl text-white" style={{ fontFamily: 'Times New Roman' }}>
          Join Tezhly <i>Career</i> Lab
        </h2>
        <div className="mt-4 flex justify-center space-x-2">
          {[1, 2].map((i) => (
            <div
              key={i}
              className={`w-2.5 h-2.5 rounded-full ${
                step >= i ? 'bg-[#800020]' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-[#2a1114] py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}

            <div className="flex justify-between">
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
                >
                  Back
                </button>
              )}
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 ml-4 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#081b1e] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-green disabled:opacity-50"
              >
                {step === 2 ? (isLoading ? 'Creating Account...' : 'Complete Registration') : 'Continue'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}